import React, { useEffect, useRef, useState } from 'react';

/**
 * MovingSprites - handles movement, rendering, and click detection for a set of sprites (e.g., prawns or invasives)
 *
 * Props:
 *  count: number of sprites
 *  images: array of image srcs (or a single src)
 *  sizeRange: [min, max] (pixels)
 *  speedRange: [min, max] (pixels/frame)
 *  zIndex: number
 *  spriteState: array of state objects per sprite (e.g., { alive: true, isFried: false })
 *  setSpriteState: setter for sprite state array
 *  onShot: function(index, x, y) - called when a sprite is shot
 *  className: string (optional)
 *  facing: array or string (optional) - 'left', 'right', or 'none' for each sprite or all
 */
const MovingSprites = ({
  count,
  images,
  sizeRange = [64, 64],
  spriteSizes,
  speedRange = [4, 4],
  zIndex = 10,
  spriteState,
  setSpriteState,
  onShot,
  className = '',
  extraProps = {},
  disabled = false,
  facing = 'none',
  blobfishImage,
  deepseacreatureImage,
  enableSpawnAnimation = false, // New prop to control spawn animation
}) => {
  const positionsRef = useRef([]);
  const sizesRef = useRef([]);
  const speedsRef = useRef([]);
  const initialDirectionsRef = useRef([]); // store initial dx/dy
  const [, setRerender] = useState(0); // force rerender
    // Prevent double firing on mobile: ignore mousedown after touchstart
    const touchHandledRef = useRef(false);
  const requestRef = useRef();
  
  // Animation state refs - moved outside useEffect to persist across re-renders
  const arcPhaseRef = useRef([]);
  const arcSpeedRef = useRef([]);
  const wobbleTimerRef = useRef([]);
  const speedModRef = useRef([]);
  const speedModTimerRef = useRef([]);
  
  // Spawn animation tracking
  const spawnTimesRef = useRef([]); // Track when each sprite was spawned
  const SPAWN_ANIMATION_DURATION = 500; // 500ms spawn animation
  
  // Use a ref to store current sprite state so animation loop can access latest state
  const spriteStateRef = useRef(spriteState);
  spriteStateRef.current = spriteState;

  // Initialize positions, sizes, speeds
  useEffect(() => {
    const currentLength = positionsRef.current.length;
    if (currentLength !== count) {
      if (currentLength < count) {
        // Adding new sprites - only initialize the new ones
        for (let i = currentLength; i < count; i++) {
          // Add new size
          const newSize = spriteSizes && spriteSizes[i] 
            ? spriteSizes[i]
            : Math.floor(sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]));
          sizesRef.current.push(newSize);
          
          // Add new speed
          const newSpeed = speedRange[0] + Math.random() * (speedRange[1] - speedRange[0]);
          speedsRef.current.push(newSpeed);
          
          // Add new position and direction
          const angle = Math.random() * 2 * Math.PI;
          const dx = Math.cos(angle) * newSpeed;
          const dy = Math.sin(angle) * newSpeed;
          const newPos = {
            x: Math.random() * (window.innerWidth - newSize),
            y: Math.random() * (window.innerHeight - newSize),
            dx,
            dy,
            _lastDx: dx, // Initialize _lastDx immediately
            _arcStrength: 0.01 + Math.random() * 0.025, // Initialize arc strength
          };
          positionsRef.current.push(newPos);
          initialDirectionsRef.current.push({ dx, dy });
          
          // Initialize animation arrays for new sprites immediately
          arcPhaseRef.current.push(Math.random() * Math.PI * 2);
          arcSpeedRef.current.push(0.003 + Math.random() * 0.004);
          wobbleTimerRef.current.push(0);
          speedModRef.current.push(1);
          speedModTimerRef.current.push(0);
          
          // Track spawn time for animation - new sprites get current time, existing ones get 0 (fully spawned)
          // Only apply spawn animation to dynamically added sprites when enabled
          const isDynamicallySpawned = currentLength > 0; // If we already had sprites, this is a dynamic spawn
          const shouldAnimate = enableSpawnAnimation && isDynamicallySpawned;
          spawnTimesRef.current.push(shouldAnimate ? Date.now() : 0);
        }
      } else {
        // Removing sprites - truncate arrays
        sizesRef.current = sizesRef.current.slice(0, count);
        speedsRef.current = speedsRef.current.slice(0, count);
        positionsRef.current = positionsRef.current.slice(0, count);
        initialDirectionsRef.current = initialDirectionsRef.current.slice(0, count);
        // Also truncate animation arrays
        arcPhaseRef.current = arcPhaseRef.current.slice(0, count);
        arcSpeedRef.current = arcSpeedRef.current.slice(0, count);
        wobbleTimerRef.current = wobbleTimerRef.current.slice(0, count);
        speedModRef.current = speedModRef.current.slice(0, count);
        speedModTimerRef.current = speedModTimerRef.current.slice(0, count);
        spawnTimesRef.current = spawnTimesRef.current.slice(0, count);
      }
    }
  }, [count, sizeRange, speedRange, spriteSizes, enableSpawnAnimation]);

  // Animate with gentle arc, rare wobble, and rare speed changes
  useEffect(() => {
    let running = true;

    const animate = () => {
      if (!running) return;
      positionsRef.current.forEach((pos, i) => {
        if (!spriteStateRef.current[i]?.alive) return;
        
        // Get animation arrays - they should already be properly sized
        const arcPhase = arcPhaseRef.current;
        const arcSpeed = arcSpeedRef.current;
        const wobbleTimer = wobbleTimerRef.current;
        const speedMod = speedModRef.current;
        const speedModTimer = speedModTimerRef.current;
        
        const size = sizesRef.current[i];
        const imgSrc = Array.isArray(images) ? images[i % images.length] : images;
        const isBlobfish = blobfishImage && imgSrc === blobfishImage;
        const isDeepSea = deepseacreatureImage && imgSrc === deepseacreatureImage;
        const isSixPackRing = imgSrc && imgSrc.includes('sixpackring.png');
        const width = (isBlobfish || isDeepSea || isSixPackRing) ? size * 2 : size;

        // --- Gentle arc movement (optimized, subtle) ---
        arcPhase[i] += arcSpeed[i];
        // Cache trigonometric values
        const sinArc = Math.sin(arcPhase[i]);
        // Use a fixed arc strength per sprite for consistency
        if (!positionsRef.current[i]._arcStrength) {
          positionsRef.current[i]._arcStrength = 0.01 + Math.random() * 0.025;
        }
        const arcStrength = positionsRef.current[i]._arcStrength;
        // Perpendicular to current direction
        const baseAngle = Math.atan2(pos.dy, pos.dx);
        const arcAngle = baseAngle + Math.PI / 2;
        // Only update arc occasionally for perf and realism
        if (Math.random() < 0.12) {
          const cosArc = Math.cos(arcAngle);
          const sinArcAngle = Math.sin(arcAngle);
          const arcDelta = sinArc * arcStrength;
          pos.dx += cosArc * arcDelta;
          pos.dy += sinArcAngle * arcDelta;
        }

        // --- Rare/occasional wobble (optimized) ---
        if (wobbleTimer[i] > 0) {
          // Wobble: quick, small random direction changes
          const wobbleX = (Math.random() - 0.5) * 0.8;
          const wobbleY = (Math.random() - 0.5) * 0.8;
          pos.dx += wobbleX;
          pos.dy += wobbleY;
          wobbleTimer[i]--;
        } else if (Math.random() < 0.002) {
          wobbleTimer[i] = 10 + (Math.random() * 10) | 0; // 10-19 frames, faster than Math.floor
        }

        // --- Rare/occasional speed up/down (optimized) ---
        if (speedModTimer[i] > 0) {
          speedModTimer[i]--;
        } else if (Math.random() < 0.0015) {
          speedMod[i] = 0.7 + Math.random() * 0.8;
          speedModTimer[i] = 30 + (Math.random() * 40) | 0; // 30-69 frames
        } else {
          speedMod[i] = 1;
        }

        // --- Normalize speed to original magnitude (with mod) ---
        const origSpeed = speedsRef.current[i];
        const mag = Math.sqrt(pos.dx * pos.dx + pos.dy * pos.dy) || 1;
        const targetSpeed = origSpeed * speedMod[i];
        pos.dx = (pos.dx / mag) * targetSpeed;
        pos.dy = (pos.dy / mag) * targetSpeed;

        // Move
        pos.x += pos.dx;
        pos.y += pos.dy;

        // Reverse direction by flipping current dx/dy, and update facing only on bounce
        // Store last dx for facing logic
        if (!pos._lastDx) pos._lastDx = pos.dx;
        let bounced = false;
        if (pos.x <= 0) {
          pos.x = 0;
          pos.dx = -pos.dx;
          bounced = true;
        } else if (pos.x >= window.innerWidth - width) {
          pos.x = window.innerWidth - width;
          pos.dx = -pos.dx;
          bounced = true;
        }
        if (pos.y <= 0) {
          pos.y = 0;
          pos.dy = -pos.dy;
          bounced = true;
        } else if (pos.y >= window.innerHeight - size) {
          pos.y = window.innerHeight - size;
          pos.dy = -pos.dy;
          bounced = true;
        }
        // Only update facing on bounce
        if (bounced) {
          pos._lastDx = pos.dx;
        }
      });
      
      // Optimized re-render strategy: Maintain 60fps smoothness while preventing CLS
      // Use CSS transforms for position updates to avoid triggering layout recalculations
      
      // Always update React state for smooth animation
      setRerender(r => r + 1);
      
      requestRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      running = false;
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Click detection
  useEffect(() => {
    if (disabled) return;
    const handleClick = (e) => {
      if (touchHandledRef.current) return;
      const clickX = e.clientX;
      const clickY = e.clientY;
      const elementAtPoint = document.elementFromPoint(clickX, clickY);
      if (className === 'prawn' && elementAtPoint && !elementAtPoint.classList.contains('prawn')) return;
      if (className === 'invasive' && elementAtPoint && !elementAtPoint.classList.contains('invasive')) return;
      for (let i = 0; i < count; i++) {
        if (!spriteStateRef.current[i]?.alive) continue;
        const pos = positionsRef.current[i];
        const size = sizesRef.current[i];
        const imgSrc = Array.isArray(images) ? images[i % images.length] : images;
        const isBlobfish = blobfishImage && imgSrc === blobfishImage;
        const isDeepSea = deepseacreatureImage && imgSrc === deepseacreatureImage;
        const width = (isBlobfish || isDeepSea) ? size * 2 : size;
        const height = size;
        if (
          clickX >= pos.x && clickX <= pos.x + width &&
          clickY >= pos.y && clickY <= pos.y + height
        ) {
          if (onShot) {
            onShot(i, clickX, clickY, e);
            if (className === 'prawn') break;
          }
        }
      }
    };
    const handleTouch = (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      const elementAtPoint = document.elementFromPoint(touchX, touchY);
      // Only preventDefault if not tapping header/nav/mute elements
      if (
        elementAtPoint && (
          elementAtPoint.classList.contains('logo') ||
          elementAtPoint.classList.contains('nav-link') ||
          elementAtPoint.classList.contains('mute-button-header') ||
          elementAtPoint.classList.contains('mute-nav-link')
        )
      ) {
        // Allow default for header/nav/mute
      } else {
        if (e.cancelable) e.preventDefault();
      }
      touchHandledRef.current = true;
      setTimeout(() => {
        touchHandledRef.current = false;
      }, 500);
      if (disabled) return;
      if (className === 'prawn' && elementAtPoint && !elementAtPoint.classList.contains('prawn')) return;
      if (className === 'invasive' && elementAtPoint && !elementAtPoint.classList.contains('invasive')) return;
      for (let i = 0; i < count; i++) {
        if (!spriteStateRef.current[i]?.alive) continue;
        const pos = positionsRef.current[i];
        const size = sizesRef.current[i];
        const imgSrc = Array.isArray(images) ? images[i % images.length] : images;
        const isBlobfish = blobfishImage && imgSrc === blobfishImage;
        const isDeepSea = deepseacreatureImage && imgSrc === deepseacreatureImage;
        const width = (isBlobfish || isDeepSea) ? size * 2 : size;
        const height = size;
        if (
          touchX >= pos.x && touchX <= pos.x + width &&
          touchY >= pos.y && touchY <= pos.y + height
        ) {
          if (onShot) {
            onShot(i, touchX, touchY, e);
            if (className === 'prawn') break;
          }
        }
      }
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('touchstart', handleTouch, { passive: false });
    // Remove previous reset logic for touchend/touchcancel
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('touchstart', handleTouch);
    };
  }, [onShot, count, disabled, images, blobfishImage, deepseacreatureImage, className]);

  // Render
  return (
    <>
      {spriteState.map((sprite, i) => {
        if (!sprite.alive) return null;
        
        // Safety check: don't render if position data isn't initialized yet
        const pos = positionsRef.current[i];
        const size = sizesRef.current[i];
        if (!pos || !size) return null;
        
        // Determine facing for this sprite
        const thisFacing = Array.isArray(facing) ? facing[i] : facing;
        // Only flip if facing is left/right and only when _lastDx is negative/positive
        let flip = false;
        if (thisFacing === 'left' && pos._lastDx > 0) flip = true;
        if (thisFacing === 'right' && pos._lastDx < 0) flip = true;
        // 'none' never flips
        // Support per-sprite style via extraStylePerSprite prop (function)
        const spriteStyle = typeof extraProps.extraStylePerSprite === 'function'
          ? extraProps.extraStylePerSprite(i)
          : (extraProps.style || {});
        
        // Calculate spawn animation progress
        const spawnTime = spawnTimesRef.current[i] || 0;
        const now = Date.now();
        const timeSinceSpawn = now - spawnTime;
        const isSpawning = enableSpawnAnimation && spawnTime > 0 && timeSinceSpawn < SPAWN_ANIMATION_DURATION;
        
        let spawnStyles = {};
        if (isSpawning) {
          // Smooth easing function (ease-out cubic)
          const progress = Math.min(timeSinceSpawn / SPAWN_ANIMATION_DURATION, 1);
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          
          // Combine fade and scale animations
          const opacity = easeProgress;
          const scale = 0.3 + (easeProgress * 0.7); // Start at 30% size, grow to 100%
          
          spawnStyles = {
            opacity,
            transform: flip ? `scaleX(-${scale})` : `scale(${scale})`,
            transition: 'opacity 0.1s ease-out', // Smooth transition for any frame drops
          };
        } else {
          // Fully spawned
          spawnStyles = {
            opacity: 1,
            transform: flip ? 'scaleX(-1)' : undefined,
          };
        }
        
        // Blobfish aspect ratio fix: width = 2 * height if image is blobfish
        const imgSrc = Array.isArray(images) ? images[i % images.length] : images;
        const isBlobfish = blobfishImage && imgSrc === blobfishImage;
        const isDeepSea = deepseacreatureImage && imgSrc === deepseacreatureImage;
        const height = size;
        const width = (isBlobfish || isDeepSea) ? height * 2 : height;
        return (
          <img
            key={i}
            src={imgSrc}
            alt="sprite"
            className={className}
            style={{
              position: 'fixed',
              left: pos.x || 0,
              top: pos.y || 0,
              width,
              height,
              zIndex,
              // CLS prevention: Use transform3d for hardware acceleration
              transform: `translate3d(0, 0, 0) ${spawnStyles.transform || (flip ? 'scaleX(-1)' : '')}`,
              opacity: spawnStyles.opacity !== undefined ? spawnStyles.opacity : 1,
              // Apply other spawn styles without transform/opacity conflicts
              transition: spawnStyles.transition,
              ...spriteStyle // User-defined styles override spawn styles if needed
            }}
            {...extraProps}
          />
        );
      })}
    </>
  );
};

export default MovingSprites;
